import type {
    IsoAndUnixTimestamp} from "@@/shared/date-time/iso-and-unix";
import {
    isoAndUnixZodSchema,
    nowIsoAndUnix,
} from "@@/shared/date-time/iso-and-unix";
import { paymentProvider } from "@@/shared/payments/payment-providers";
import { userIdZodSchema } from "@@/shared/users/user-id";
import { z } from "zod";
import type { PayRequestId } from "../pay-request-id";
import { payRequestPaymentIdZodSchema } from "../pay-request-payment-id";
import type { PayRequestEvent } from "./_pay-request.event";
import { payRequestEventIdFactory } from "./_pay-request.event-id";
import { payRequestEventSchemaFactory } from "./_pay-request.event.schema-factory";

const payRequestPaymentCancelledEventZodObject = payRequestEventSchemaFactory(
    "PAY_REQUEST_PAYMENT_CANCELLED_EVENT",
    z
        .object({
            cancelledBy: z.union([
                userIdZodSchema,
                z.literal("PAYMENT_PROVIDER"),
            ]),
            cancelledAt: isoAndUnixZodSchema,
            reason: z.string().optional(),
            payRequestPaymentId: payRequestPaymentIdZodSchema,
            type: z.literal("PAY_REQUEST_PAYMENT_CANCELLED_EVENT"),
        })
        .and(
            z.discriminatedUnion("paymentProvider", [
                z.object({
                    paymentProvider: z.literal(paymentProvider.STRIPE),
                    stripePaymentId: z.string(),
                }),
                z.object({
                    paymentProvider: z.literal(paymentProvider.SWISH),
                    swishPaymentUUID: z.string(),
                }),
            ]),
        ),
);
type PayRequestPaymentCancelledEvent = z.infer<
    typeof payRequestPaymentCancelledEventZodObject
>;
const payRequestPaymentCancelledEventZodSchema: z.ZodSchema<PayRequestPaymentCancelledEvent> =
    payRequestPaymentCancelledEventZodObject;

type PayRequestPaymentCancelledEventFactoryParams = {
    payRequestId: PayRequestId;
    happenedAt: IsoAndUnixTimestamp;
    data: PayRequestPaymentCancelledEvent["data"];
    paymentFor: PayRequestEvent["paymentFor"];
    version: number;
};
const payRequestPaymentCancelledEventFactory = (
    params: PayRequestPaymentCancelledEventFactoryParams,
) => {
    const event: PayRequestPaymentCancelledEvent = {
        _id: payRequestEventIdFactory(),
        _type: "PAY_REQUEST_EVENT",
        _createdAt: nowIsoAndUnix(),
        _happenedAt: params.happenedAt,
        _version: params.version,
        type: "PAY_REQUEST_PAYMENT_CANCELLED_EVENT",
        data: params.data,
        payRequestId: params.payRequestId,
        paymentFor: params.paymentFor,
    };
    return payRequestPaymentCancelledEventZodSchema.parse(event);
};

export {
    payRequestPaymentCancelledEventFactory,
    payRequestPaymentCancelledEventZodObject,
    payRequestPaymentCancelledEventZodSchema,
};
export type { PayRequestPaymentCancelledEvent };
